import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import {
  EMPTY_SIGNER,
  fetchEmployeeSignerProfile,
  normalizeSigners,
} from "./signatureSignerUtils";

export { normalizeSigners } from "./signatureSignerUtils";

export default function SignatureSignersEditor({ signers, onChange, disabled = false }) {
  const { t } = useTranslation();
  const rows = normalizeSigners(signers);
  const [loadingIndex, setLoadingIndex] = useState(null);

  const updateRows = (next) => onChange(next);

  const updateRow = (index, patch) => {
    updateRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateRowField = (index, field, value) => updateRow(index, { [field]: value });

  const addRow = () => updateRows([...rows, { ...EMPTY_SIGNER }]);

  const removeRow = (index) => {
    if (rows.length <= 1) {
      updateRows([{ ...EMPTY_SIGNER }]);
      return;
    }
    updateRows(rows.filter((_, i) => i !== index));
  };

  const handleEmployeeSelect = async (index, event) => {
    const userId = event?.target?.value ?? event?.target?.object?.id;
    if (!userId) {
      updateRow(index, { ...EMPTY_SIGNER });
      return;
    }

    setLoadingIndex(index);
    try {
      const profile = await fetchEmployeeSignerProfile(userId);
      updateRow(index, {
        ...profile,
        label: rows[index]?.label || "",
      });
    } catch {
      updateRow(index, {
        user_id: userId,
        employee_label: event?.target?.object?.displayTitle || "",
        name: event?.target?.object?.displayTitle || "",
      });
    } finally {
      setLoadingIndex(null);
    }
  };

  const clearEmployee = (index) => {
    updateRow(index, {
      ...EMPTY_SIGNER,
      label: rows[index]?.label || "",
    });
  };

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">
          {t("textDocuments.signature.signers")}
        </h4>
        <p className="mt-1 text-xs text-slate-500">
          {t("textDocuments.signature.signers_hint")}
        </p>
      </div>

      <div className="space-y-4">
        {rows.map((row, index) => {
          const linkedEmployee = Boolean(row.user_id || row.employee_id);
          const isLoading = loadingIndex === index;

          return (
            <div
              key={index}
              className="space-y-3 rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-[220px] flex-1">
                  <EmployeeSelector
                    noLabel={false}
                    label={t("textDocuments.signature.select_employee")}
                    name={`signature_signer_${index}_user_id`}
                    selected={
                      row.user_id
                        ? { id: row.user_id, displayTitle: row.employee_label || row.name }
                        : undefined
                    }
                    onChange={(event) => handleEmployeeSelect(index, event)}
                    disabled={disabled || isLoading}
                  />
                </div>
                {linkedEmployee ? (
                  <button
                    type="button"
                    disabled={disabled || isLoading}
                    onClick={() => clearEmployee(index)}
                    className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t("textDocuments.signature.manual_entry")}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeRow(index)}
                  className="rounded-full p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  title={t("textDocuments.signature.remove_signer")}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              {isLoading ? (
                <p className="text-xs text-slate-500">{t("textDocuments.signature.loading_employee")}</p>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    {t("textDocuments.signature.name")}
                  </label>
                  <input
                    type="text"
                    value={row.name}
                    disabled={disabled || linkedEmployee}
                    onChange={(e) => updateRowField(index, "name", e.target.value)}
                    placeholder={t("textDocuments.signature.name_placeholder")}
                    className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    {t("textDocuments.signature.job_title")}
                  </label>
                  <input
                    type="text"
                    value={row.title}
                    disabled={disabled || linkedEmployee}
                    onChange={(e) => updateRowField(index, "title", e.target.value)}
                    placeholder={t("textDocuments.signature.job_title_placeholder")}
                    className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    {t("textDocuments.signature.field_label")}
                  </label>
                  <input
                    type="text"
                    value={row.label}
                    disabled={disabled}
                    onChange={(e) => updateRowField(index, "label", e.target.value)}
                    placeholder={t("textDocuments.signature.field_label_placeholder")}
                    className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {!linkedEmployee ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600">
                      {t("textDocuments.signature.national_identity_number")}
                    </label>
                    <input
                      type="text"
                      value={row.national_identity_number}
                      disabled={disabled}
                      onChange={(e) =>
                        updateRowField(index, "national_identity_number", e.target.value)
                      }
                      placeholder={t("textDocuments.signature.national_identity_number_placeholder")}
                      autoComplete="off"
                      className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600">
                      {t("textDocuments.signature.email")}
                    </label>
                    <input
                      type="email"
                      value={row.email}
                      disabled={disabled}
                      onChange={(e) => updateRowField(index, "email", e.target.value)}
                      placeholder={t("textDocuments.signature.email_placeholder")}
                      className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600">
                      {t("textDocuments.signature.mobile")}
                    </label>
                    <input
                      type="text"
                      value={row.mobile}
                      disabled={disabled}
                      onChange={(e) => updateRowField(index, "mobile", e.target.value)}
                      placeholder={t("textDocuments.signature.mobile_placeholder")}
                      className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  {t("textDocuments.signature.employee_posten_hint")}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={addRow}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
      >
        <PlusIcon className="h-4 w-4" />
        {t("textDocuments.signature.add_signer")}
      </button>
    </div>
  );
}
